import { type CSSProperties, type ReactNode, useCallback, useEffect, useState } from 'react';

import {
  type BrandColour,
  type BrandOverrideField,
  type BrandOverrides,
  type BrandProfile,
  type BrandView,
  fetchBrandBookBrand,
  refreshBrandBookBrand,
  type Repository,
  saveBrandBookOverrides,
} from './api.ts';
import { describeError, redirectIfUnauthorised } from './data.tsx';
import { Icon } from './Icon.tsx';
import { localTime, since } from './schedule.ts';
import { useToast } from './toast.tsx';
import { Badge, EmptyState, Loader, Markdown, Notice, Panel, Skeleton } from './ui.tsx';

/**
 * A brand book's brand profile, as something to judge at a glance (US-069,
 * on the brand book page since US-012).
 *
 * The profile is the input to every later plan, so the question this panel
 * answers is not "did the extraction run" but "would I ship a video that
 * looked and sounded like this". That is why the colours are swatches rather
 * than hex strings, the fonts are set in the font they name, and the tagline
 * and claims are shown as the product's own words — a wrong claim is visible
 * as a sentence and invisible as a JSON field.
 *
 * The row is the source of truth: a *failed* refresh keeps the profile it
 * already had, so a failure is a notice above a profile that still stands,
 * never an empty panel.
 *
 * What the panel offers follows the book's source. A repository or website
 * book is an extraction with the operator's corrections over it, so a
 * corrected field shows what was found beneath it. A book typed by hand has
 * nothing underneath: every field is the operator's own, edited in place, and
 * there is nothing to refresh.
 */

/** A hex the swatch is prepared to paint. Anything else is shown as text. */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export function BrandPanel({
  brandBookId,
  repository,
}: {
  readonly brandBookId: string;
  /** The repository a repository book is cloned from, when this page knows it: its key gates **Refresh**. */
  readonly repository?: Repository;
}) {
  const toast = useToast();
  const [view, setView] = useState<BrandView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        setView(await fetchBrandBookBrand(brandBookId, signal));
        setError(null);
      } catch (cause: unknown) {
        if (signal?.aborted === true) return;
        if (redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      }
    },
    [brandBookId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /**
   * The refresh *is* the extraction: the request is held open for the whole
   * container, so there is nothing to poll and the button stays busy until
   * there is an answer. A second press while one runs joins the first on the
   * server rather than paying for a second agent run.
   */
  const onRefresh = (): void => {
    setRefreshing(true);
    const name = view?.brandBookName ?? 'this brand book';
    refreshBrandBookBrand(brandBookId)
      .then((result) => {
        if (result.status === 'ready') {
          toast.ok(
            result.coalesced
              ? `${name}: joined the extraction already running.`
              : `Extracted the brand profile for ${name}.`,
          );
        } else {
          toast.error(result.message);
        }
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setRefreshing(false);
        void load();
      });
  };

  // While a refresh runs the server's own status is `extracting`, so the two
  // agree even in a second tab that did not press the button.
  const extracting = refreshing || view?.refreshing === true || view?.status === 'extracting';
  const profile = view?.profile ?? null;
  const manual = view?.sourceType === 'manual';
  // A repository book needs its repository's key to clone with; a book whose
  // repository is gone cannot be refreshed at all (the server says so too).
  const missingSource =
    view?.sourceType === 'repository'
      ? view.repositoryId === null
        ? 'The repository this brand book was extracted from is deleted.'
        : repository !== undefined && !repository.keyConfigured
          ? 'This repository has no key to clone with.'
          : null
      : view?.sourceType === 'website' && view.websiteUrl === null
        ? 'This brand book has no website address to read.'
        : null;

  return (
    <Panel
      title="Brand profile"
      icon="paintbrush"
      className="brand"
      meta={
        <>
          <BrandBadge status={view?.status ?? 'none'} extracting={extracting} />
          {view !== null && !manual && view.overriddenFields > 0 && (
            <Badge tone="active">
              {view.overriddenFields} corrected
            </Badge>
          )}
        </>
      }
      actions={
        manual ? null : (
          <button
            type="button"
            className="button button--small"
            onClick={onRefresh}
            disabled={extracting || view === null || !view.available || missingSource !== null}
            title={
              view?.available === false
                ? 'This server cannot start containers, so it cannot extract a profile.'
                : (missingSource ?? undefined)
            }
          >
            <Icon name={extracting ? 'sync' : 'paintbrush'} />
            {extracting ? 'Extracting…' : profile === null ? 'Extract brand profile' : 'Refresh'}
          </button>
        )
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {view === null ? (
        <Skeleton lines={4} />
      ) : (
        <div className="stack">
          {extracting && (
            <Loader
              label={
                view.sourceType === 'website'
                  ? 'Extracting the brand profile: taking a snapshot of the site and reading it…'
                  : `Extracting the brand profile: cloning ${repository?.defaultBaseBranch ?? 'the default branch'} and reading it…`
              }
              detail={
                view.sourceType === 'website'
                  ? 'Two containers, a few minutes; the profile below is whatever the last extraction found until this one lands.'
                  : 'One container, a minute or two; the profile below is whatever the last extraction found until this one lands.'
              }
            />
          )}
          {view.status === 'failed' && view.error !== null && (
            <Notice kind="error">
              The last refresh failed: {view.error}
              {profile !== null && ' The profile below is the one that worked before it.'}
            </Notice>
          )}
          {view.sourceType === 'website' && <WebsiteSource view={view} />}
          {profile === null ? (
            manual ? (
              <EmptyState icon="paintbrush" title="Nothing typed yet">
                <p>
                  This brand book is typed by hand: fill in the fields below — the name at least —
                  and every content made in it reads them as its brand.
                </p>
              </EmptyState>
            ) : (
              <BrandEmpty view={view} />
            )
          ) : (
            <BrandProfileView
              profile={profile}
              extractedAt={view.extractedAt}
              sourceType={view.sourceType}
            />
          )}
          <BrandOverridesSection view={view} onSaved={setView} />
          {view.markdown !== null && view.markdown.trim() !== '' && (
            <details className="disclosure">
              <summary className="disclosure__summary">
                <Icon name="chevron-right" />
                brand.md
              </summary>
              <div className="disclosure__body">
                <p className="field__hint">
                  The readable companion as every later planning and copy prompt reads it —
                  the extraction with the corrections below merged into it.
                </p>
                <Markdown text={view.markdown} />
              </div>
            </details>
          )}
        </div>
      )}
    </Panel>
  );
}

function BrandBadge({
  status,
  extracting,
}: {
  readonly status: BrandView['status'];
  readonly extracting: boolean;
}) {
  if (extracting) {
    return (
      <Badge tone="active">
        extracting
      </Badge>
    );
  }
  if (status === 'failed') return <Badge tone="danger">failed</Badge>;
  if (status === 'ready') return <Badge tone="done">ready</Badge>;
  return <Badge tone="neutral">no profile</Badge>;
}

/** What an extraction would read, in the server's own words. */
function BrandEmpty({ view }: { readonly view: BrandView }) {
  return (
    <EmptyState icon="paintbrush" title="No brand profile yet">
      <p>
        <strong>Extract brand profile</strong> reads this brand book’s source and writes down what the
        product looks and sounds like, so every plan starts on-brand instead of guessing. It reads:
      </p>
      <ul className="brand__reads">
        {view.reads.map((line) => (
          <li key={line}>
            <Markdown text={line} />
          </li>
        ))}
      </ul>
      <p className="muted">{view.help}</p>
    </EmptyState>
  );
}

function BrandProfileView({
  profile,
  extractedAt,
  sourceType,
}: {
  readonly profile: BrandProfile;
  readonly extractedAt: string | null;
  readonly sourceType: BrandView['sourceType'];
}) {
  // Only a website book's sources are pages; anything else is a path, even
  // one that happens to look like a URL.
  const linked = sourceType === 'website';
  const visual = profile.visual ?? {};
  const product = profile.product ?? {};
  const voice = profile.voice ?? {};
  const moments = profile.product_moments ?? [];
  const screens = profile.screens ?? [];
  const sources = profile.sources ?? [];
  const swatches: BrandColour[] = [
    ...named('background', visual.background),
    ...named('surface', visual.surface),
    ...named('text', visual.text),
    ...named('accent', visual.accent),
    ...(visual.palette ?? []),
  ];

  return (
    <div className="stack">
      <div className="brand__top">
        <BrandLogo profile={profile} manual={sourceType === 'manual'} />
        <div className="brand__identity">
          {product.name !== undefined && (
            <p className="brand__name" style={fontOf(visual.displayFont)}>
              {product.name}
            </p>
          )}
          {product.tagline !== undefined && (
            <p className="brand__tagline" style={fontOf(visual.displayFont)}>
              {product.tagline}
            </p>
          )}
          {product.oneLiner !== undefined && (
            <p className="brand__one-liner" style={fontOf(visual.bodyFont)}>
              {product.oneLiner}
            </p>
          )}
        </div>
      </div>

      {swatches.length > 0 && (
        <BrandSection label="Palette">
          <ul className="swatches">
            {swatches.map((colour, index) => (
              <Swatch colour={colour} key={`${colour.hex ?? colour.name ?? ''}-${String(index)}`} />
            ))}
          </ul>
          {visual.gradients !== undefined && visual.gradients.length > 0 && (
            <p className="field__hint mono">{visual.gradients.join(' · ')}</p>
          )}
        </BrandSection>
      )}

      {(visual.displayFont ?? visual.bodyFont ?? visual.monoFont) !== undefined && (
        <BrandSection label="Type">
          <ul className="brand__fonts">
            <FontLine role="Display" family={visual.displayFont} />
            <FontLine role="Body" family={visual.bodyFont} />
            <FontLine role="Mono" family={visual.monoFont} mono />
          </ul>
          {visual.fontSources !== undefined && visual.fontSources.length > 0 && (
            <p className="field__hint">
              Loaded from <span className="mono">{visual.fontSources.join(', ')}</span>
            </p>
          )}
        </BrandSection>
      )}

      {product.claims !== undefined && product.claims.length > 0 && (
        <BrandSection label="Claims">
          <ul className="brand__claims">
            {product.claims.map((claim) => (
              <li key={claim}>{claim}</li>
            ))}
          </ul>
        </BrandSection>
      )}

      {(voice.tone ?? voice.register ?? voice.sampleLines ?? voice.bannedPhrases) !== undefined && (
        <BrandSection label="Tone">
          <p className="brand__tone">
            {[voice.tone, voice.register].filter((part) => part !== undefined).join(' · ')}
          </p>
          {(voice.sampleLines ?? []).map((line) => (
            <blockquote className="brand__sample" key={line}>
              {line}
            </blockquote>
          ))}
          {voice.bannedPhrases !== undefined && voice.bannedPhrases.length > 0 && (
            <p className="field__hint">
              Never says: <span className="mono">{voice.bannedPhrases.join(', ')}</span>
            </p>
          )}
        </BrandSection>
      )}

      {moments.length > 0 && (
        <BrandSection label="Moments">
          <ul className="brand__claims">
            {moments.map((moment, index) => (
              <li key={`${moment.title ?? ''}-${String(index)}`}>
                {moment.title !== undefined && <strong>{moment.title}</strong>}
                {moment.whatToShow !== undefined && <> — {moment.whatToShow}</>}
                <SourceList sources={moment.sourcePaths ?? []} linked={linked} />
              </li>
            ))}
          </ul>
        </BrandSection>
      )}

      {screens.length > 0 && (
        <BrandSection label="Screens">
          <ul className="brand__claims">
            {screens.map((screen, index) => (
              <li key={`${screen.route ?? ''}-${String(index)}`}>
                {screen.route !== undefined && <span className="mono">{screen.route}</span>}
                {screen.description !== undefined && <> — {screen.description}</>}
                <SourceList
                  sources={screen.sourcePath === undefined ? [] : [screen.sourcePath]}
                  linked={linked}
                />
              </li>
            ))}
          </ul>
        </BrandSection>
      )}

      {sources.length > 0 && (
        <BrandSection label="Sources">
          <ul className="brand__sources">
            {sources.map((source) => (
              <li key={source}>
                <Source source={source} linked={linked} />
              </li>
            ))}
          </ul>
        </BrandSection>
      )}

      <p className="brand__extracted muted">
        <Icon name="clock" />
        {sourceType === 'manual' ? (
          'Typed by hand.'
        ) : extractedAt === null ? (
          'Never extracted.'
        ) : (
          <>
            Extracted {since(extractedAt)}{' '}
            <span title={localTime(extractedAt)}>({localTime(extractedAt)})</span>
          </>
        )}
        {sourceType !== 'manual' && sources.length > 0 && (
          <>
            {' '}
            from {sources.length} {linked && sources.every(isPageUrl) ? 'page' : 'file'}
            {sources.length === 1 ? '' : 's'}
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Where a moment or a screen was read from: a path in the repository, or — for
 * a brand book extracted from a website (US-005) — the page's URL, which is
 * printed as a link to it.
 */
function SourceList({
  sources,
  linked,
}: {
  readonly sources: readonly string[];
  readonly linked: boolean;
}) {
  if (sources.length === 0) return null;
  return (
    <span className="field__hint">
      {' '}
      (
      {sources.map((source, index) => (
        <span key={source}>
          {index > 0 && ', '}
          <Source source={source} linked={linked} />
        </span>
      ))}
      )
    </span>
  );
}

/** One source: a link for a website book's page URL, a path otherwise. */
function Source({ source, linked }: { readonly source: string; readonly linked: boolean }) {
  if (linked && isPageUrl(source)) {
    return (
      <a className="link" href={source} target="_blank" rel="noreferrer">
        {source}
      </a>
    );
  }
  return <span className="mono">{source}</span>;
}

/**
 * Where a website book reads from: the address the operator gave, and the
 * pages the last snapshot actually read — the profile is only as good as
 * those, and a site whose robots.txt or redirects kept the crawl to one page
 * says so here rather than in a thinner profile nobody can explain.
 */
function WebsiteSource({ view }: { readonly view: BrandView }) {
  const snapshot = view.snapshot;
  return (
    <BrandSection label="Website">
      <p>
        {view.websiteUrl === null ? (
          <span className="muted">No address.</span>
        ) : (
          <a className="link" href={view.websiteUrl} target="_blank" rel="noreferrer">
            {view.websiteUrl}
          </a>
        )}
      </p>
      {snapshot === null ? (
        <p className="field__hint">
          {view.status === 'ready'
            ? 'The pages the profile was read from are listed after the next refresh.'
            : 'No snapshot has been read yet.'}
        </p>
      ) : (
        <>
          <p className="field__hint">
            The last snapshot{snapshot.url === view.websiteUrl ? '' : <> of <span className="mono">{snapshot.url}</span></>}{' '}
            read {snapshot.pages.length} page{snapshot.pages.length === 1 ? '' : 's'},{' '}
            <span title={localTime(snapshot.takenAt)}>{since(snapshot.takenAt)}</span>:
          </p>
          <ul className="brand__sources">
            {snapshot.pages.map((page) => (
              <li key={page.url}>
                <a className="link" href={page.url} target="_blank" rel="noreferrer">
                  {page.title.trim() === '' ? page.url : page.title}
                </a>{' '}
                {page.title.trim() !== '' && <span className="field__hint mono">{page.url}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </BrandSection>
  );
}

function isPageUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/**
 * The mark, as far as the profile knows it.
 *
 * The extraction records the *path* of the logo and never copies the file —
 * the clone is thrown away with the container — so there is no image to show
 * here, and pretending otherwise would be a broken `<img>` on every panel.
 * What can be shown honestly is the wordmark: the product's name set in its
 * display font on its own background and accent, which is the thing the
 * operator is actually judging. The path is printed beneath it so the file the
 * profile points at is still checkable.
 */
function BrandLogo({
  profile,
  manual,
}: {
  readonly profile: BrandProfile;
  readonly manual: boolean;
}) {
  const visual = profile.visual ?? {};
  const background = paintable(visual.background) ?? paintable(visual.surface);
  const foreground = paintable(visual.text);
  const accent = paintable(visual.accent);
  const mark = (profile.product?.name ?? profile.product?.tagline ?? '—').trim();
  const logoPath = visual.logoPaths?.[0] ?? visual.iconPaths?.[0] ?? null;

  return (
    <figure className="brand__logo">
      <div
        className="brand__mark"
        style={{
          ...(background === null ? {} : { background }),
          ...(foreground === null ? {} : { color: foreground }),
          ...(accent === null ? {} : { borderColor: accent }),
          ...(visual.radius === undefined ? {} : { borderRadius: visual.radius }),
          ...fontOf(visual.displayFont),
        }}
      >
        <span className="brand__mark-initial" style={accent === null ? {} : { color: accent }}>
          {mark.slice(0, 1).toUpperCase()}
        </span>
        <span className="brand__mark-word">{mark}</span>
      </div>
      <figcaption className="field__hint">
        {logoPath === null ? (
          manual ? (
            'No logo file named.'
          ) : (
            'No logo file found by the extraction.'
          )
        ) : (
          <>
            <span className="mono">{logoPath}</span> —{' '}
            {manual ? 'the file named for this brand.' : 'the file the extraction found.'} Its
            bytes stay with the source, so this is the wordmark in the profile’s own colours and
            font.
          </>
        )}
      </figcaption>
    </figure>
  );
}

function BrandSection({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="brand__section">
      <h3 className="brand__label">{label}</h3>
      {children}
    </section>
  );
}

function Swatch({ colour }: { readonly colour: BrandColour }) {
  const hex = paintable(colour.hex);
  const label = colour.name ?? colour.role ?? colour.hex ?? '';
  return (
    <li className="swatch" title={[label, colour.hex, colour.role].filter(Boolean).join(' · ')}>
      <span
        className={`swatch__chip${hex === null ? ' swatch__chip--unknown' : ''}`}
        style={hex === null ? {} : { background: hex }}
        aria-hidden="true"
      />
      <span className="swatch__name">{label}</span>
      {colour.hex !== undefined && <span className="swatch__hex mono">{colour.hex}</span>}
    </li>
  );
}

function FontLine({
  role,
  family,
  mono = false,
}: {
  readonly role: string;
  readonly family: string | undefined;
  readonly mono?: boolean;
}) {
  if (family === undefined || family.trim() === '') return null;
  return (
    <li className="brand__font">
      <span className="brand__font-role">{role}</span>
      {/* Set in the font it names, with the repository's own stack as the
          fallback: a font the browser does not have reads as the default and
          still says which one was asked for. */}
      <span className="brand__font-name" style={fontOf(family, mono)}>
        {family}
      </span>
    </li>
  );
}

/** The one named colour as a palette entry, or nothing. */
function named(name: string, hex: string | undefined): BrandColour[] {
  return hex === undefined || hex.trim() === '' ? [] : [{ name, hex, role: name }];
}

/** A colour only when it is a hex this can paint; never arbitrary CSS. */
function paintable(value: string | undefined): string | null {
  if (value === undefined) return null;
  const trimmed = value.trim();
  return HEX.test(trimmed) ? trimmed : null;
}

/**
 * The family as a CSS stack. The extraction records what the repository
 * declares, which may be a stack already; the fallback keeps an unavailable
 * family readable rather than leaving it to the browser's serif default.
 */
function fontOf(family: string | undefined, mono = false): CSSProperties {
  if (family === undefined || family.trim() === '') return {};
  const fallback = mono ? 'var(--font-mono)' : 'var(--font-sans)';
  const quoted = family.includes(',') || family.includes('"') ? family : `"${family}"`;
  return { fontFamily: `${quoted}, ${fallback}` };
}

/**
 * The operator's corrections, edited in place (US-070).
 *
 * Every field the server says it edits, with the **merged** value in the box —
 * so what is shown is what every plan will read — and the extraction's own
 * value printed underneath whenever the two differ. Clearing the box removes
 * the correction rather than storing an empty string, which is the only way
 * back to what the repository said.
 *
 * The list of fields comes off the view rather than out of this file: the API
 * accepts any field the brand schema has, and the page must not offer one it
 * would refuse or miss one it would take.
 *
 * A book typed by hand (US-012) is the same overrides with nothing under
 * them, so the same fields are its whole profile: shown open as **Fields**,
 * with no "corrected" marks and no extraction to fall back to — clearing a
 * field just empties it.
 */
function BrandOverridesSection({
  view,
  onSaved,
}: {
  readonly view: BrandView;
  readonly onSaved: (view: BrandView) => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState<string | null>(null);
  const manual = view.sourceType === 'manual';

  const save = (field: BrandOverrideField, value: string | string[] | null): void => {
    setSaving(field.path);
    saveBrandBookOverrides(view.brandBookId, patchFor(field.path, value))
      .then((next) => {
        onSaved(next);
        toast.ok(
          manual
            ? value === null
              ? `${field.label} cleared for ${view.brandBookName}.`
              : `${field.label} saved for ${view.brandBookName}.`
            : value === null
              ? `${field.label} is back to what the extraction found.`
              : `${field.label} corrected for ${view.brandBookName}.`,
        );
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setSaving(null);
      });
  };

  const sections = [...new Set(view.overrideFields.map((field) => field.section))];

  return (
    <details className="disclosure" open={manual ? true : undefined}>
      <summary className="disclosure__summary">
        <Icon name="chevron-right" />
        {manual ? 'Fields' : 'Corrections'}
        {!manual && view.overriddenFields > 0 && (
          <span className="muted"> ({view.overriddenFields})</span>
        )}
      </summary>
      <div className="disclosure__body stack">
        <p className="field__hint">
          {manual
            ? 'This brand book is typed by hand, so these fields are its whole brand profile — every plan, prompt and workspace of a content made in it reads them. Leave anything you do not know empty.'
            : view.overridesHelp}
        </p>
        {sections.map((section) => (
          <section className="brand__section" key={section}>
            <h3 className="brand__label">{section}</h3>
            <div className="brand__overrides">
              {view.overrideFields
                .filter((field) => field.section === section)
                .map((field) => (
                  <OverrideInput
                    key={field.path}
                    field={field}
                    merged={stringAt(view.profile, field.path, field.kind)}
                    extracted={stringAt(view.extracted, field.path, field.kind)}
                    overridden={valueAt(view.overrides, field.path) !== undefined}
                    manual={manual}
                    busy={saving === field.path}
                    onSave={(value) => {
                      save(field, value);
                    }}
                  />
                ))}
            </div>
          </section>
        ))}
      </div>
    </details>
  );
}

/**
 * One field.
 *
 * The box is uncontrolled between saves — it is seeded from the merged value
 * and reseeded whenever that changes — so a refresh landing in another tab
 * does not overwrite something half-typed here. **Save** only appears once the
 * text differs from what is stored, and **Revert** only once there is a
 * correction to remove.
 */
function OverrideInput({
  field,
  merged,
  extracted,
  overridden,
  manual,
  busy,
  onSave,
}: {
  readonly field: BrandOverrideField;
  readonly merged: string;
  readonly extracted: string;
  readonly overridden: boolean;
  /** A hand-typed book: the value is the field, not a correction of one. */
  readonly manual: boolean;
  readonly busy: boolean;
  readonly onSave: (value: string | string[] | null) => void;
}) {
  const [draft, setDraft] = useState(merged);
  useEffect(() => {
    setDraft(merged);
  }, [merged]);

  const dirty = draft !== merged;
  const id = `override-${field.path.replace('.', '-')}`;
  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed === '') {
      onSave(null);
      return;
    }
    onSave(field.kind === 'lines' ? splitLines(trimmed) : trimmed);
  };

  return (
    <div className="field brand__override">
      <label className="field__label" htmlFor={id}>
        {field.label}
        {overridden && !manual && <span className="brand__corrected">corrected</span>}
      </label>
      {field.kind === 'lines' ? (
        <textarea
          id={id}
          className="field__input"
          rows={3}
          value={draft}
          disabled={busy}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
      ) : (
        <input
          id={id}
          className="field__input"
          type="text"
          value={draft}
          disabled={busy}
          placeholder={field.kind === 'colour' ? '#000000' : ''}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && dirty) commit();
          }}
        />
      )}
      <div className="brand__override-actions">
        {dirty && (
          <button type="button" className="button button--small" disabled={busy} onClick={commit}>
            <Icon name={busy ? 'sync' : 'check'} />
            Save
          </button>
        )}
        {overridden && !dirty && (
          <button
            type="button"
            className="button button--small button--quiet"
            disabled={busy}
            onClick={() => {
              onSave(null);
            }}
          >
            {manual ? 'Clear' : 'Revert'}
          </button>
        )}
        {!manual && overridden && extracted !== '' && extracted !== merged && (
          <span className="field__hint">
            The extraction found <span className="mono">{oneLine(extracted)}</span>.
          </span>
        )}
      </div>
    </div>
  );
}

/** `{product: {tagline: value}}` — the partial body the `PUT` takes. */
function patchFor(path: string, value: string | string[] | null): BrandOverrides {
  const [group, name] = path.split('.');
  if (group === undefined || name === undefined) return {};
  return { [group]: { [name]: value } } as BrandOverrides;
}

/** The value at a dotted path, or `undefined`. */
function valueAt(source: BrandProfile | BrandOverrides | null, path: string): unknown {
  let cursor: unknown = source;
  for (const part of path.split('.')) {
    if (typeof cursor !== 'object' || cursor === null || Array.isArray(cursor)) return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

/** The same value as something an input can hold; a list is one line each. */
function stringAt(
  source: BrandProfile | null,
  path: string,
  kind: BrandOverrideField['kind'],
): string {
  const value = valueAt(source, path);
  if (kind === 'lines') return Array.isArray(value) ? value.map(String).join('\n') : '';
  return typeof value === 'string' ? value : '';
}

/** A textarea back into a list: one entry per non-empty line. */
function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/** A multi-line value on one line, for the "what was found" hint. */
function oneLine(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > 80 ? `${flat.slice(0, 80)}…` : flat;
}
